import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { SourceBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Inbox, Check, X, Users, Clock, MessageCircle } from "lucide-react";

export default function Leads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reservations", { params: { status: "richiesta" } });
      setItems(data.filter((r) => r.source === "portale"));
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, status) => {
    try {
      await api.patch(`/reservations/${id}/status`, { status });
      toast.success(status === "confermata" ? "Lead confermato — WhatsApp inviato ✅" : "Lead rifiutato");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="leads-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-primary">Ingresso lead</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Lead da turismo.latina.it</h1>
        <p className="text-slate-500 text-sm mt-1">Richieste dei turisti arrivate dal portale, in attesa di conferma.</p>
      </div>

      {loading ? <p className="text-slate-400 py-12 text-center">Caricamento...</p> :
        items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
            <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">Nessun nuovo lead dal portale.</p>
            <p className="text-xs text-slate-400 mt-1">Prova la pagina demo del portale per generarne uno.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {items.map((r) => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid={`lead-card-${r.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <SourceBadge source={r.source} />
                  <span className="flex items-center gap-1 text-xs text-amber-700 font-medium"><Clock className="h-3.5 w-3.5" /> In attesa</span>
                </div>
                <p className="font-semibold text-slate-900 text-lg">{r.customer_name}</p>
                <div className="text-sm text-slate-500 mt-1 space-y-0.5">
                  <p className="flex items-center gap-2"><Users className="h-3.5 w-3.5" /> {r.party_size} {r.checkin ? "ospiti" : "coperti"}</p>
                  <p>{r.checkin ? `${r.checkin} → ${r.checkout}` : `${r.date} · ${r.time}`}</p>
                  <p>{r.customer_phone}</p>
                  {r.notes && <p className="italic text-slate-400">"{r.notes}"</p>}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" onClick={() => act(r.id, "confermata")} className="rounded-lg flex-1" data-testid={`lead-accept-${r.id}`}><Check className="h-4 w-4 mr-1" /> Accetta</Button>
                  <Button size="sm" variant="outline" onClick={() => act(r.id, "cancellata")} className="rounded-lg text-destructive" data-testid={`lead-reject-${r.id}`}><X className="h-4 w-4 mr-1" /> Rifiuta</Button>
                </div>
                <p className="text-[11px] text-emerald-600 mt-2 flex items-center gap-1"><MessageCircle className="h-3 w-3" /> Alla conferma parte il WhatsApp automatico</p>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
